import React from 'react';
import { Layers } from 'lucide-react';
import { KindBadge } from './registry';
import { ArtifactActions } from './ArtifactActions';
import type { WorkspaceUiArtifact } from './types';

interface Props {
  artifact: WorkspaceUiArtifact;
  onOpen: (id: string) => void;
}

function extractThumbUrl(artifact: WorkspaceUiArtifact): string | null {
  if (artifact.kind !== 'image') return null;
  if (!artifact.content || typeof artifact.content !== 'object') return null;
  const url = (artifact.content as { url?: unknown }).url;
  return typeof url === 'string' && url.length > 0 ? url : null;
}

// Task #3846 — root non più <button>: le azioni Scarica/Condividi annidate
// richiedono un contenitore div con role="button" (bottoni annidati in un
// <button> sono HTML invalido e rompono il click).
export const ArtifactMiniCard: React.FC<Props> = ({ artifact, onOpen }) => {
  const thumbUrl = extractThumbUrl(artifact);
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(artifact.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(artifact.id);
        }
      }}
      title="Apri nel pannello artefatti"
      className="group w-full max-w-[80%] cursor-pointer text-left rounded-2xl border border-violet-200 bg-violet-50/70 px-3 py-2 transition-colors hover:border-violet-300 hover:bg-violet-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"
    >
      <div className="flex min-w-0 items-center gap-2">
        {thumbUrl ? (
          <img
            src={thumbUrl}
            alt={artifact.title}
            loading="lazy"
            className="h-9 w-9 shrink-0 rounded-lg object-cover ring-1 ring-violet-200"
          />
        ) : (
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600">
            <Layers className="h-3.5 w-3.5" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <KindBadge kind={artifact.kind} />
            <span className="truncate text-sm font-medium text-foreground">{artifact.title}</span>
            {artifact.isFresh && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500" />}
            <span className="ml-auto shrink-0 text-[10px] font-normal text-muted-foreground">v{artifact.version}</span>
          </div>
          {artifact.summary && (
            <div className="truncate text-xs text-muted-foreground">{artifact.summary}</div>
          )}
        </div>
        <ArtifactActions artifact={artifact} />
      </div>
    </div>
  );
};
